<template>
  <div class="pricing-categories-editor" :class="'pce--' + layout">
    <div v-if="title" class="pce__head">
      <p class="section-label mb-0">{{ title }}</p>
    </div>

    <slot></slot>

    <div v-if="layout === 'rows'" class="pce__columns">
      <span>Category</span>
      <span>Pricing</span>
      <span>Participant limits</span>
      <span></span>
    </div>

    <div v-for="(cat, catIdx) in categories" :key="setKey + 'CAT' + catIdx" class="pce__category">
      <div class="pce__row">
        <base-input
          class="pce__name mb-0"
          :label="layout === 'rows' ? 'Name' : 'Category name'"
          placeholder="e.g. Adults, Children, Seniors"
          v-model="cat.category"
          :error="errors[catIdx] && errors[catIdx].category"
        />
        <button
          v-if="layout !== 'rows'"
          class="row-remove pce__remove-category"
          type="button"
          aria-label="Remove category"
          :disabled="categories.length <= 1"
          @click="removeCategory(catIdx)"
        >
          <i class="tim-icons icon-simple-remove"></i>
        </button>
        <div class="pce__limits">
          <base-input label="Min required" class="form-field--compact pce__limit mb-0" :error="errors[catIdx] && errors[catIdx].minimum_participants">
            <el-input-number size="small" :controls="false" v-model.number="cat.minimum_participants" :min="0" :step="1" />
          </base-input>
          <base-input label="Max allowed" class="form-field--compact pce__limit mb-0" :error="errors[catIdx] && errors[catIdx].maximum_participants">
            <el-input-number size="small" :controls="false" :value="cat.maximum_participants == null ? undefined : cat.maximum_participants" placeholder="No limit" :step="1" @input="v => { cat.maximum_participants = v == null ? null : v }" />
          </base-input>
          <base-input label="Default" class="form-field--compact pce__limit mb-0" :error="errors[catIdx] && errors[catIdx].default_participants">
            <el-input-number size="small" :controls="false" v-model.number="cat.default_participants" :step="1" />
          </base-input>
        </div>
      </div>

      <button
        v-if="layout === 'rows'"
        class="row-remove pce__remove-category"
        type="button"
        aria-label="Remove category"
        :disabled="categories.length <= 1"
        @click="removeCategory(catIdx)"
      >
        <i class="tim-icons icon-simple-remove"></i>
      </button>

      <div class="pce__tiers">
        <div class="pce__tier-head">
          <span>Participants</span>
          <span>Price</span>
          <span></span>
        </div>
        <template v-for="(tier, tierIdx) in cat.pricing">
          <div :key="'TIER' + tierIdx" class="pce__tier">
            <range-input
              size="sm"
              :min="toNumber(tier.min_players)"
              :max="toNumber(tier.max_players)"
              :min-floor="getMinPlayers(cat.pricing, tierIdx)"
              :error="!!(tierError(catIdx, tierIdx, 'min_players') || tierError(catIdx, tierIdx, 'max_players'))"
              @update:min="v => { tier.min_players = v }"
              @update:max="v => { tier.max_players = v }"
            />
            <money-input
              size="sm"
              :value="toNumber(tier.price)"
              :min="0"
              :step="0.1"
              :error="!!tierError(catIdx, tierIdx, 'price')"
              @input="v => { tier.price = v }"
            />
            <button
              class="row-remove"
              type="button"
              aria-label="Remove tier"
              :disabled="cat.pricing.length <= 1"
              @click="removeTier(cat.pricing, tierIdx)"
            >
              <i class="tim-icons icon-simple-remove"></i>
            </button>
          </div>
          <small v-if="tierMessage(catIdx, tierIdx)" :key="'TIERERR' + tierIdx" class="pce__tier-error">{{ tierMessage(catIdx, tierIdx) }}</small>
        </template>
        <button class="pce__add-tier" type="button" @click="addTier(cat)">
          <i class="tim-icons icon-simple-add"></i> Add tier
        </button>
      </div>
    </div>

    <div class="pce__foot">
      <base-button size="sm" variant="outlined" tone="primary" @click="addCategory">
        <i class="tim-icons icon-simple-add"></i> {{ addLabel }}
      </base-button>
    </div>
  </div>
</template>

<script setup lang="ts">
// Shared pricing categories editor: category name and participant limits on one
// line, then the price tiers. The `categories` array is edited in place (same
// convention as the booking drawer views); the host owns validation and
// persistence. Errors use the shape returned by validateSlotPricing in
// src/util/slotPricingValidation.js. A category always keeps at least one tier
// and the list always keeps at least one category: nothing priced can be saved empty.
interface Tier { min_players: number | null; max_players: number | null; price: number | string | null }
interface Category {
  category: string
  pricing: Tier[]
  minimum_participants: number | null
  maximum_participants: number | null | undefined
  default_participants: number | null
  [key: string]: any
}

const props = withDefaults(defineProps<{
  categories: Category[]
  errors?: Record<number, any>
  title?: string
  addLabel?: string
  /** Changes the row keys so switching the backing array (duration pills) remounts the rows */
  setKey?: string
  /** 'stacked' (drawers): name and limits on one line, tiers below. 'rows' (pages): one grid row per category. */
  layout?: 'stacked' | 'rows'
}>(), { errors: () => ({}), title: 'Categories', addLabel: 'Add category', setKey: 'default', layout: 'stacked' })

function toNumber(v: any): number | null {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function getMinPlayers(pricing: Tier[], index: number): number {
  if (index === 0) return 1
  const prev = Number(pricing[index - 1].max_players)
  return Number.isFinite(prev) ? prev + 1 : 1
}

function addCategory() {
  props.categories.push({
    category: '',
    pricing: [{ min_players: 1, max_players: 10, price: 0 }],
    minimum_participants: 0,
    maximum_participants: null,
    default_participants: 1
  })
}

function removeCategory(index: number) {
  if (props.categories.length > 1) props.categories.splice(index, 1)
}

function addTier(cat: Category) {
  const last = cat.pricing[cat.pricing.length - 1]
  const lastMax = last ? Number(last.max_players) : 0
  cat.pricing.push({
    min_players: (Number.isFinite(lastMax) ? lastMax : 0) + 1,
    max_players: (Number.isFinite(lastMax) ? lastMax : 0) + 5,
    price: 0
  })
}

function removeTier(pricing: Tier[], index: number) {
  if (pricing.length > 1) pricing.splice(index, 1)
}

function tierError(catIdx: number, tierIdx: number, field: string): string | undefined {
  const c = props.errors ? props.errors[catIdx] : undefined
  return c && c.tiers && c.tiers[tierIdx] ? c.tiers[tierIdx][field] : undefined
}

// One line under the row: the first message among price, min and max
function tierMessage(catIdx: number, tierIdx: number): string {
  return tierError(catIdx, tierIdx, 'price') || tierError(catIdx, tierIdx, 'min_players') || tierError(catIdx, tierIdx, 'max_players') || ''
}
</script>

<script lang="ts">
export default { name: 'PricingCategoriesEditor' }
</script>

<style lang="scss" scoped>
.pricing-categories-editor {
  max-width: 760px;
}

.pce__head {
  margin-bottom: 12px;
}

.section-label {
  text-transform: uppercase;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.pce__category {
  padding: 16px 0 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  &:first-of-type {
    border-top: 0;
    padding-top: 4px;
  }
}

// Name + remove + three limits on one line; limits wrap under the name when narrow
.pce__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 14px;
}

.pce__name {
  flex: 1 1 220px;
  min-width: 0;
}

.pce__remove-category {
  margin-bottom: 8px;
}

.pce__limits {
  // The three limits move as one unit: on the name's line when it fits, else all three on the next line
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 100%;
}

.pce__limit {
  width: 112px;
}

// Page layout: a header once, then one grid row per category (name | tiers | limits | remove)
.pce--rows {
  max-width: none;

  .pce__columns,
  .pce__category {
    display: grid;
    grid-template-columns: minmax(200px, 280px) minmax(380px, max-content) max-content 24px;
    gap: 12px 28px;
    align-items: start;
  }

  .pce__columns {
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.6);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .pce__category {
    padding: 14px 0 16px;

    &:first-of-type {
      border-top: 0;
      padding-top: 14px;
    }
  }

  .pce__row {
    display: contents;
  }

  .pce__name {
    grid-column: 1;
    grid-row: 1;
  }

  .pce__tiers {
    grid-column: 2;
    grid-row: 1;
    margin-top: 0;
  }

  .pce__limits {
    grid-column: 3;
    grid-row: 1;
    display: flex;
    gap: 10px;
  }

  .pce__remove-category {
    grid-column: 4;
    grid-row: 1;
    margin-top: 26px;
  }

  @media (max-width: 1100px) {
    .pce__columns { display: none; }
    // Stacked: name with its remove X on the first line, the three limits as equal columns on the
    // second, tiers on the third. minmax(0, 1fr) keeps content from pushing the row past the card.
    .pce__category { grid-template-columns: minmax(0, 1fr) 24px; gap: 12px 10px; }
    .pce__name { grid-column: 1; grid-row: 1; min-width: 0; }
    .pce__remove-category { grid-column: 2; grid-row: 1; align-self: end; margin: 0 0 8px; }
    .pce__limits { grid-column: 1 / -1; grid-row: 2; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); width: 100%; min-width: 0; }
    .pce__limit { width: auto; min-width: 0; }
    .pce__tiers { grid-column: 1 / -1; grid-row: 3; margin-top: 4px; min-width: 0; }
  }
}

.pce__tiers {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pce__tier-head,
.pce__tier {
  display: grid;
  grid-template-columns: 180px 140px 24px;
  gap: 10px;
  align-items: center;
}

.pce__tier-head {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.pce__tier-error {
  display: block;
  color: #ec250d;
  font-size: 11px;
  margin-top: -2px;
}

.pce__add-tier {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  padding: 2px 0;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;

  i { font-size: 11px; }

  &:hover { color: #e14eca; }
}

.pce__foot {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 560px) {

  // Phone width: range and price share the row fluidly instead of fixed tracks that overflow the card
  .pce__tier-head,
  .pce__tier {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr) 24px;
  }

  .pce__tiers {
    min-width: 0;
  }
}
</style>

<style lang="scss">
// Page layout sits on the lighter card background, where the drawer's translucent fill reads as a dark
// block; match the surrounding inputs (transparent) there. Drawers keep the fill. Light mode stays white.
.pricing-categories-editor.pce--rows {
  .range-input,
  .amount-input-group {
    background: transparent;
  }
}

.pricing-categories-editor {
  .pce__limit .el-input-number--small {
    width: 100%;
  }

  .pce__limit .el-input-number--small .el-input__inner {
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
}

.white-content .pricing-categories-editor {
  .section-label { color: #525f7f; }
  .pce__columns { color: #8898aa; border-bottom-color: #e9ecef; }
  .pce__category { border-top-color: #e9ecef; }
  .pce__foot { border-top-color: #e9ecef; }
  .pce__tier-head { color: #8898aa; }
  .pce__add-tier { color: #8898aa; &:hover { color: #e14eca; } }
}
</style>
